import {BigCalendarUser} from 'platform/big-calendar';

import {GetDataByDataQueryApiResponse} from '@dms/api/core';
import i18n from '@dms/i18n';

import {Nullish, parseDate} from 'shared/web';

import {getTestDriveUserColorVariants} from './getTestDriveUserColorVariants';
import {TestDriveCalendarEvent} from './TestDriveCalendarEvent';

const DEFAULT_DURATION_MS = 30 * 60 * 1000;

export function getBigCalendarDataFromTestDriveGrid(
  dataGridData: GetDataByDataQueryApiResponse | Nullish,
  selectedUsers: BigCalendarUser[] | Nullish
) {
  return dataGridData?.rows?.reduce<TestDriveCalendarEvent[]>((events, row) => {
    const scheduledAt: string | null = row.cells.scheduledAt?.value;
    const returnedAt: string | null = row.cells.returnedAt?.value;
    const state: {key: 'planned' | 'completed' | 'cancelled'} | null = row.cells.state?.value;
    const driver: {id: string} | null = row.cells.driver?.value;
    const customer: {id: string; label: string} | null = row.cells.customer?.value;
    const vehicle: {id: string} | null = row.cells.vehicle?.value;
    const businessCase: {id: string; label: string} | null = row.cells.businessCase?.value;
    const note: string | null = row.cells.note?.value;

    const startDate = parseDate(scheduledAt);

    if (!startDate || !driver?.id) {
      return events;
    }

    const isUserSelected = selectedUsers?.some((user) => user.id === driver.id) ?? false;

    if (!isUserSelected) {
      return events;
    }

    const driverIndex = selectedUsers?.findIndex((user) => user.id === driver.id);
    const testDriveColorVariants = getTestDriveUserColorVariants(driverIndex);

    const endDate = returnedAt
      ? parseDate(returnedAt)
      : parseDate(startDate.getTime() + DEFAULT_DURATION_MS);

    const title =
      customer?.label ?? businessCase?.label ?? i18n.t('entity.businessCase.labels.testDrive');

    const isCompleted = state?.key === 'completed' || state?.key === 'cancelled';

    events.push({
      id: row.id,
      title,
      start: startDate,
      end: endDate,
      color: testDriveColorVariants.USER,
      icon: 'maps/directions_car',
      alternative: isCompleted,
      hideEndTime: !returnedAt,
      eventType: 'testDrive',
      testDriveData: {
        userId: driver.id,
        state: state?.key ?? 'planned',
        note,
        scheduledAt: scheduledAt ?? startDate.toISOString(),
        returnedAt: returnedAt ?? null,
        hasReturnedAt: returnedAt !== null,
      },
      businessCaseId: businessCase?.id ?? null,
      vehicleId: vehicle?.id ?? '',
      customerId: customer?.id ?? '',
    });

    return events;
  }, []);
}
